import { approvalsNotificationListMaxLimit } from '@gonfalon/dogfood-flags/dogfood-flags';
import { fromJS, get, isImmutable, List, Map, OrderedMap, Record } from '@gonfalon/immutable-shim/immutableShim';
import {
  isLength,
  isNotEmpty,
  isNotZero,
  isValidNotifyMembersList,
  validateRecord,
} from '@gonfalon/legacy-form-validation/validationUtils';
import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';
import { msg } from '@gonfalon/messages/msg';
import { parse as resourceSpecifierFromString } from '@gonfalon/resource-specifiers/parser';
import { isPlainJSONObject } from '@gonfalon/types/isJSONValue';

import { combineAccessDecisions } from '#utils/access/combineAccessDecisions';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { CheckAccessFunction } from '#utils/access/types';
import type { ApprovalSettings } from '#utils/environments/records/ApprovalSettings';
import { createApprovalRequestSettings } from '#utils/environments/records/ApprovalSettings';
import type { Environment } from '#utils/environments/records/Environment';
import { createEnvironment } from '#utils/environments/records/Environment';
import { createResourceApprovalRequestSettings } from '#utils/environments/records/ResourceApprovalSettings';
import { ApprovalServiceKind } from '#utils/environments/types';
import { createFlag, Flag } from '#utils/flags/records/Flag';
import { FlagVariationsInstructionKind } from '#utils/instructions/variations/types';
import type { Member } from '#utils/members/records/Member';
import { createProject } from '#utils/projects/records/Project';
import { createSegment, Segment } from '#utils/segment/records/Segment';

import { AiConfig } from '../../aiConfigUtils';
import { makeSemanticInstruction } from '../../instructions/shared/makeSemanticInstruction';
import {
  ApprovalProgressStatus,
  ApprovalRequestKind,
  ApprovalsReviewKind,
  ApprovalsReviewStatus,
  ApprovalWorkflowExecutionStatus,
  DESCRIPTION_MAX_LENGTH,
} from '../constants';
import { getApprovalRequestResourceKind } from '../getApprovalRequestResourceKind';
import { reviewKindToApprovalReviewStatus } from '../reviewKindToApprovalReviewStatus';
import {
  type ApprovalRequestProps,
  type ApprovalRequestResourceKind,
  ExperimentApprovalResource,
  type ResourceTypeName,
  type ReviewerType,
} from '../types';

import { createApprovalCustomWorkflowMetadata } from './ApprovalCustomWorkflowMetadata';
import { createApprovalRequestReview } from './ApprovalRequestReview';

export class ApprovalRequest extends Record<ApprovalRequestProps>({
  _id: '',
  _links: Map(),
  reviewStatus: ApprovalsReviewStatus.DEFAULT,
  creationDate: 0,
  requestorId: '',
  description: '',
  status: '',
  appliedByMemberId: '',
  appliedByServiceTokenId: undefined,
  operatingOnId: undefined,
  allReviews: List(),
  notifyMemberIds: List(),
  executionDate: 0,
  appliedDate: 0,
  instructions: List(),
  conflicts: List(),
  serviceKind: ApprovalServiceKind.LaunchDarklyApprovalServiceKind,
  integrationMetadata: Map(),
  source: undefined,
  customWorkflowMetadata: undefined,
  project: undefined,
  environment: undefined,
  environments: undefined,
  flag: undefined,
  approvalSettings: undefined,
  resourceApprovalSettings: undefined,
  resourceId: undefined,
  resource: undefined,
}) {
  getCreationDate() {
    return this.creationDate;
  }
  getId() {
    return this._id;
  }

  getInstructions() {
    return this.instructions;
  }

  getConflicts() {
    return this.conflicts || List();
  }

  getReviews() {
    return this.allReviews.sort((a, b) => {
      const dateA = a.creationDate;
      const dateB = b.creationDate;
      return dateA - dateB; // oldest to newest
    });
  }

  getNotifiedMemberIds() {
    return this.notifyMemberIds;
  }

  getResourceKind(): ApprovalRequestResourceKind {
    return getApprovalRequestResourceKind(this.resourceId);
  }

  getResourceKindName(): ResourceTypeName {
    if (!this.resourceId) {
      return 'flag';
    }

    const result = resourceSpecifierFromString(this.resourceId);

    if (result.err) {
      return 'resource';
    }

    return result.val.type;
  }

  getReadableResourceKindName(): string {
    const resourceKindName = this.getResourceKindName();
    return resourceKindName === 'aiconfig' ? msg('approvals.resourceKind.aiConfig') : resourceKindName;
  }

  isIntegrationApproval() {
    return this.serviceKind !== '' && this.serviceKind !== ApprovalServiceKind.LaunchDarklyApprovalServiceKind;
  }

  isCustomApproval() {
    return (
      this.serviceKind !== '' &&
      [ApprovalServiceKind.CustomApprovalServiceKind, ApprovalServiceKind.CustomApprovalV2ServiceKind].includes(
        this.serviceKind as ApprovalServiceKind,
      )
    );
  }

  isFlagCopyApproval() {
    return this.source !== undefined;
  }

  isApproved() {
    return this.reviewStatus === ApprovalsReviewStatus.APPROVED;
  }

  isApplied() {
    return !!this.appliedDate;
  }

  isDeclined() {
    return this.reviewStatus === ApprovalsReviewStatus.DECLINED;
  }

  validate({
    approvalServiceKind = ApprovalServiceKind.LaunchDarklyApprovalServiceKind,
    isScheduled = false,
  }: { approvalServiceKind?: ApprovalServiceKind | string; isScheduled?: boolean } = {}) {
    const predicates = [
      isNotEmpty('description'),
      isLength(0, DESCRIPTION_MAX_LENGTH)('description'),
      isNotEmpty('instructions'),
    ];
    if (approvalServiceKind === ApprovalServiceKind.LaunchDarklyApprovalServiceKind) {
      predicates.push(isNotEmpty('notifyMemberIds'));
      predicates.push(isValidNotifyMembersList(approvalsNotificationListMaxLimit())('notifyMemberIds'));
    }
    if (isScheduled) {
      predicates.push(isNotZero('executionDate'));
    }
    return validateRecord(this, ...predicates);
  }

  /**
   * Returns a map keyed by memberId indicating the review status of that member
   * Review status will be determined by the latest review
   * unless that review is a comment type and is preceded by an approve or decline type
   */
  makeReviewerToReviewStatus() {
    const reviewerToReviewStatus = this.getReviews().reduce(
      (accum, curr) => {
        const memberId = curr.memberId;
        const serviceTokenId = curr.serviceTokenId;
        const reviewerType = memberId ? ('member' as ReviewerType) : ('serviceToken' as ReviewerType);
        const reviewerId = memberId ? memberId : serviceTokenId;
        const reviewStatus = curr.kind;
        if (!reviewerId) {
          return accum;
        }
        return reviewStatus === ApprovalsReviewKind.COMMENT && accum.hasOwnProperty(reviewerId)
          ? accum
          : { ...accum, [reviewerId]: { reviewStatus: reviewKindToApprovalReviewStatus[reviewStatus], reviewerType } };
      },
      {} as { [reviewer: string]: { reviewStatus: ApprovalsReviewStatus; reviewerType: ReviewerType } },
    );

    this.getNotifiedMemberIds().forEach((memberId) => {
      reviewerToReviewStatus[memberId] = reviewerToReviewStatus[memberId] ?? ApprovalsReviewStatus.DEFAULT;
    });

    return reviewerToReviewStatus;
  }

  /**
   * Returns the most recent approve or decline review for a given member id
   * If none found, returns undefined
   */
  getLatestApproveOrDeclineForMemberId(memberId?: string) {
    if (!memberId) {
      return undefined;
    }
    return this.getReviews().findLast(
      (review) => review.get('kind') !== ApprovalsReviewKind.COMMENT && review.get('memberId') === memberId,
    );
  }

  getHasMemberReviewed(memberId: string) {
    return !!this.getLatestApproveOrDeclineForMemberId(memberId);
  }

  getHasMemberApproved(memberId: string) {
    return this.getLatestApproveOrDeclineForMemberId(memberId)?.kind === ApprovalsReviewKind.APPROVE;
  }

  getHasMemberDeclined(memberId: string) {
    return this.getLatestApproveOrDeclineForMemberId(memberId)?.kind === ApprovalsReviewKind.DECLINE;
  }

  getResourceCheckAccess({
    resource,
    environment,
    profile,
  }: {
    resource: Flag | Segment | AiConfig | ExperimentApprovalResource;
    environment?: Environment;
    profile: Member;
  }) {
    let checkAccess = (_action?: string) => createAccessDecision({ isAllowed: false });

    if (resource instanceof Flag) {
      checkAccess = environment
        ? resource.checkAccess({ envKey: environment.key, profile })
        : resource.checkAccessForAllEnvironments({ profile });
    }

    if (resource instanceof Segment) {
      checkAccess = resource.checkAccess(profile);
    }

    if (resource instanceof AiConfig) {
      checkAccess = resource.checkAccess(profile);
    }

    if (resource instanceof ExperimentApprovalResource) {
      checkAccess = resource.checkAccess(profile);
    }

    return checkAccess;
  }

  getProfileCommentAccess({
    profile,
    environment,
    approvalSettings,
    resource,
  }: {
    profile: Member;
    environment?: Environment;
    approvalSettings: ApprovalSettings; // TODO add support for ResourceApprovalSettings
    resource?: Flag | Segment | AiConfig | ExperimentApprovalResource;
  }) {
    let checkAccess = () => createAccessDecision({ isAllowed: false });

    if (resource) {
      checkAccess = this.getResourceCheckAccess({ resource, environment, profile });
    }

    return this.createCommentAccess({
      checkAccess,
      memberId: profile._id,
      canReviewOwnRequest: approvalSettings.canReviewOwnRequest,
      isIntegrationApproval: this.isIntegrationApproval(),
    });
  }

  getProfileReviewAccess({
    profile,
    environment,
    approvalSettings,
    resource,
  }: {
    profile: Member;
    environment?: Environment;
    approvalSettings: ApprovalSettings; // TODO add support for ResourceApprovalSettings
    resource?: Flag | Segment | AiConfig | ExperimentApprovalResource;
  }) {
    let checkAccess = () => createAccessDecision({ isAllowed: false });

    if (resource) {
      checkAccess = this.getResourceCheckAccess({ resource, environment, profile });
    }

    return this.createReviewAccess({
      checkAccess,
      memberId: profile._id,
      canReviewOwnRequest: approvalSettings.canReviewOwnRequest,
      isIntegrationApproval: this.isIntegrationApproval(),
    });
  }

  getWasMemberNotified(memberId: string) {
    return this.getNotifiedMemberIds().includes(memberId);
  }

  getProfileUpdateAccess({
    profile,
    environment,
    resource,
  }: {
    profile: Member;
    environment?: Environment;
    resource: Flag | Segment | AiConfig | ExperimentApprovalResource;
  }) {
    if (resource) {
      const checkAccess = this.getResourceCheckAccess({ resource, environment, profile });
      return checkAccess('updateApprovalRequest');
    }

    return createAccessDecision({ isAllowed: false });
  }

  getProfileApplyAccess({
    profile,
    environment,
    approvalSettings,
    resource,
  }: {
    profile: Member;
    environment?: Environment;
    approvalSettings: ApprovalSettings; // TODO add support for ResourceApprovalSettings
    resource: Flag | Segment | AiConfig | ExperimentApprovalResource;
  }) {
    let checkAccess = () => createAccessDecision({ isAllowed: false });

    if (resource) {
      checkAccess = this.getResourceCheckAccess({ resource, environment, profile });
    }

    return this.createApplyAccess({
      checkAccess,
      minNumberOfApprovals: approvalSettings.minNumApprovals || 1,
      canApplyDeclinedChanges: approvalSettings.canApplyDeclinedChanges,
      isIntegrationApproval: this.isIntegrationApproval(),
    });
  }

  getProfileDeleteAccess({
    profile,
    environment,
    resource,
  }: {
    profile: Member;
    environment?: Environment;
    resource: Flag | Segment | AiConfig | ExperimentApprovalResource;
  }) {
    let checkAccess = () => createAccessDecision({ isAllowed: false });

    if (resource) {
      checkAccess = this.getResourceCheckAccess({ resource, environment, profile });
    }

    return this.createDeleteAccess({ checkAccess });
  }

  getProgressStatus() {
    if (this.isApplied()) {
      return this.status === ApprovalWorkflowExecutionStatus.FAILED
        ? ApprovalProgressStatus.FAILED
        : ApprovalProgressStatus.COMPLETED;
    }

    if (this.reviewStatus === ApprovalsReviewStatus.APPROVED) {
      return ApprovalProgressStatus.APPROVED;
    }

    if (this.reviewStatus === ApprovalsReviewStatus.DECLINED) {
      return ApprovalProgressStatus.DECLINED;
    }

    return ApprovalProgressStatus.PENDING;
  }

  /**
   * Returns a count of how many members have a given review status
   * This indicates how many reviews of a given kind are factoring into
   * whether the approval request is considered "approved" or "declined"
   */
  getReviewStatusCount(reviewStatus: ApprovalsReviewStatus) {
    const reviewerToReviewStatus = this.makeReviewerToReviewStatus();
    return Object.values(reviewerToReviewStatus).filter((review) => review.reviewStatus === reviewStatus).length;
  }

  /**
   * Returns the kind of approval request based on what resources will be impacted after applying
   */
  getKind() {
    if (this.operatingOnId) {
      // at the moment only workflows for deleting scheduled changes have an `operatingOnId` property
      return ApprovalRequestKind.DELETE_SCHEDULED_CHANGE;
    }
    if (this.executionDate) {
      return ApprovalRequestKind.SCHEDULE_CHANGES;
    }
    return ApprovalRequestKind.UPDATE_FLAG;
  }

  /**
   * Returns an access decision determining whether or not a member can review an approval request by declining or approving
   */
  createReviewAccess({
    checkAccess,
    memberId,
    canReviewOwnRequest,
    isIntegrationApproval,
  }: {
    checkAccess: CheckAccessFunction;
    memberId?: string;
    canReviewOwnRequest: boolean;
    isIntegrationApproval: boolean;
  }) {
    const isOwnerOfRequest = memberId && memberId === this.requestorId;
    return combineAccessDecisions(
      checkAccess('reviewApprovalRequest'),
      createAccessDecision({
        isAllowed: !isOwnerOfRequest || canReviewOwnRequest,
        customMessage: msg('approvals.reviewAccess.ownRequest'),
      }),
      createAccessDecision({
        isAllowed: !isIntegrationApproval,
      }),
    );
  }

  /**
   * Returns an access decision determining whether or not a member can comment on an approval request
   */
  createCommentAccess({
    checkAccess,
    memberId,
    canReviewOwnRequest,
    isIntegrationApproval,
  }: {
    checkAccess: CheckAccessFunction;
    memberId?: string;
    canReviewOwnRequest: boolean;
    isIntegrationApproval: boolean;
  }) {
    const reviewAccess = this.createReviewAccess({
      checkAccess,
      memberId,
      canReviewOwnRequest,
      isIntegrationApproval,
    });
    const isOwnerOfRequest = memberId && memberId === this.requestorId;
    return combineAccessDecisions(
      createAccessDecision({
        isAllowed: reviewAccess.isAllowed || isOwnerOfRequest,
        customMessage: reviewAccess.getActionReason(),
      }),
      createAccessDecision({
        isAllowed: !isIntegrationApproval,
      }),
    );
  }

  /**
   * Returns an access decision determining whether or not a member can apply an approval request
   */
  createApplyAccess({
    checkAccess,
    minNumberOfApprovals,
    canApplyDeclinedChanges,
    isIntegrationApproval,
  }: {
    checkAccess: CheckAccessFunction;
    minNumberOfApprovals: number;
    canApplyDeclinedChanges: boolean;
    isIntegrationApproval: boolean;
  }) {
    const isDeclined = this.reviewStatus === ApprovalsReviewStatus.DECLINED;
    const isApproved = this.reviewStatus === ApprovalsReviewStatus.APPROVED;
    const numApprovalsLeft = minNumberOfApprovals - this.getReviewStatusCount(ApprovalsReviewStatus.APPROVED);
    const hasConflicts = this.getConflicts().size > 0;

    return combineAccessDecisions(
      checkAccess('applyApprovalRequest'),
      createAccessDecision({
        isAllowed: !isDeclined,
        customMessage:
          isIntegrationApproval || canApplyDeclinedChanges
            ? msg('approvals.applyAccess.declined')
            : msg('approvals.applyAccess.declinedNotAllowed'),
      }),
      createAccessDecision({
        isAllowed: isApproved,
        customMessage: msg('approvals.applyAccess.approvalsRemaining', { count: numApprovalsLeft }),
      }),
      createAccessDecision({
        isAllowed: !hasConflicts,
        customMessage: isApproved
          ? msg('approvals.applyAccess.unresolvedConflicts')
          : msg('approvals.applyAccess.conflictsWarning'),
      }),
    );
  }

  /**
   * Returns an access decision determining whether or not an approval request can be deleted
   */
  createDeleteAccess({ checkAccess }: { checkAccess: CheckAccessFunction }) {
    return combineAccessDecisions(
      checkAccess('deleteApprovalRequest'),
      createAccessDecision({
        isAllowed: !this.isApplied(),
        customMessage: msg('approvals.deleteAccess.alreadyApplied'),
      }),
    );
  }
}

function preserveInstructionValueOrder(props: CreateFunctionInput<ApprovalRequest>) {
  const instructions = isImmutable(props) ? props.get('instructions') : props.instructions;
  return Array.isArray(instructions)
    ? instructions.map((i) => {
        if (
          i.kind === FlagVariationsInstructionKind.ADD_VARIATION ||
          i.kind === FlagVariationsInstructionKind.UPDATE_VARIATION
        ) {
          let value = i.value;
          if (isPlainJSONObject(i.value)) {
            value = OrderedMap(i.value);
          }

          // Only add the value property if it was modified or already existed
          if (value !== i.value) {
            return { ...i, value };
          }
        }

        return i;
      })
    : instructions;
}

export const createApprovalRequest = (props: CreateFunctionInput<ApprovalRequest> = {}) => {
  if (props instanceof ApprovalRequest) {
    return props;
  }

  let approvalRequest = new ApprovalRequest(fromJS(props));

  approvalRequest = approvalRequest.set(
    'allReviews',
    approvalRequest.get('allReviews').map(createApprovalRequestReview),
  );

  const instructions = preserveInstructionValueOrder(props);
  approvalRequest = approvalRequest.set('instructions', fromJS(instructions));
  approvalRequest = approvalRequest.update('instructions', (vs) =>
    vs.size > 0 ? vs.map((v) => makeSemanticInstruction(get(v, 'kind', ''), v)) : List(),
  );

  // transform the conflicts the same way so we can compare them with instructions
  approvalRequest = approvalRequest.update('conflicts', (values) =>
    values?.size > 0
      ? values.map((conflict) =>
          conflict.get('instruction')
            ? conflict.update('instruction', (i) => makeSemanticInstruction(get(i, 'kind', ''), i))
            : conflict,
        )
      : List(),
  );

  if (approvalRequest.get('customWorkflowMetadata')) {
    approvalRequest = approvalRequest.update('customWorkflowMetadata', (cwMetadata) =>
      createApprovalCustomWorkflowMetadata(cwMetadata),
    );
  }

  if (approvalRequest.get('project')) {
    approvalRequest = approvalRequest.update('project', (p) => createProject(p));
  }

  if (approvalRequest.get('environment')) {
    approvalRequest = approvalRequest.update('environment', (e) => createEnvironment(e));
  }

  if (approvalRequest.get('environments')) {
    approvalRequest = approvalRequest.update('environments', (envs) => envs?.map(createEnvironment));
  }

  if (approvalRequest.get('approvalSettings')) {
    approvalRequest = approvalRequest.update('approvalSettings', (approvalSettings) =>
      createApprovalRequestSettings(approvalSettings),
    );
  }

  if (approvalRequest.get('resourceApprovalSettings')) {
    approvalRequest = approvalRequest.update('resourceApprovalSettings', (approvalSettings) =>
      createResourceApprovalRequestSettings(approvalSettings),
    );
  }

  if (approvalRequest.get('flag')) {
    approvalRequest = approvalRequest.update('flag', (f) => createFlag(f));
  }

  if (approvalRequest.get('resource')) {
    if (approvalRequest.getIn(['resource', 'kind']) === 'flag') {
      approvalRequest = approvalRequest.updateIn(['resource', 'flag'], (f) => createFlag(f));
    }

    if (approvalRequest.getIn(['resource', 'kind']) === 'segment') {
      approvalRequest = approvalRequest.updateIn(['resource', 'segment'], (s) => createSegment(s));
    }
  }

  return approvalRequest;
};
